import { isEqual, max } from 'date-fns';
import { atom } from 'jotai';
import { atomFamily, atomWithDefault, loadable } from 'jotai/utils';
import {
  ICompanyFinancialsDataModel,
  KpiPeriod,
  KpiSection,
} from '../../../data-models/company-financials.data-model';
import { fetchCompanyFinancials } from '../../../services/queries/MaggieCompanyQueries';
import { selectedFinancialsCurrencyForIdAtom } from '../components/Financials/hooks/FinancialsCurrencyState';
import { getCompanySyncIfResolved } from '../../../services/state/CompanyStateJ';
import { kpiConfigByIdMapAtom } from '../../../services/state/KPIConfigState';
import { extractISODate, isDateString } from '../../../util/date-utilts';
import { endOfFiscalPeriod, isEqualToKpiDate } from '../utils/financialUtils';
import { getTypedFinancials, TypedFinancials } from '../utils/getTypedFinancials';
import { RendererType } from '../../../data-models/field-definition';

// financials item date fields should always be last day of period
export type ICompanyFinancialsByPeriodKey = {
  companyId: number;
  date: string;
  period: KpiPeriod;
};

/**
 * Per-company financials (atomFamily, async-default fetch).
 *
 * Sentinel guard: `companyId <= 0` returns an empty array — many consumers
 * default to -1 via `selectedCompanyIdProfileAtom`.
 */
export const allCompanyFinancialsState = atomFamily((companyId: number) =>
  atomWithDefault<ICompanyFinancialsDataModel[] | Promise<ICompanyFinancialsDataModel[]>>(async (get) => {
    if (companyId <= 0) {
      return [];
    }
    // Read the selected currency reactively so switching it re-runs this fetch.
    // (atomWithDefault RESET alone will not refetch when the atom was never overwritten.)
    const currencyCode = get(selectedFinancialsCurrencyForIdAtom(companyId));
    const financials = await fetchCompanyFinancials({ companyId, currencyCode });
    return filterOutInvalidFinancials(financials ?? []).map((item) => ({
      ...item,
      date: extractISODate(item.date),
    }));
  })
);

export const allCompanyFinancialsStateLoadable = atomFamily((companyId: number) =>
  loadable(allCompanyFinancialsState(companyId))
);

/**
 * Per-period typed financials for a company. Now a pure Jotai derivation
 * that reactively reads {@link kpiConfigByIdMapAtom} — replaces the PR 12a
 * `getForesightStore().get(kpiConfigByIdMapAtom)` non-reactive bridge.
 */
export const typedFinancialsByPeriodState = atomFamily(
  ({ companyId, date, period }: ICompanyFinancialsByPeriodKey) =>
    atom<Promise<TypedFinancials[]>>(async (get) => {
      const kpiConfigMap = await get(kpiConfigByIdMapAtom);
      const allCompanyFinancials = await get(allCompanyFinancialsState(companyId));
      const companyFinancialsForMonth = allCompanyFinancials.filter((item) => item.date === date);
      const company = getCompanySyncIfResolved(companyId);
      const fye = company?.fye ?? 12;
      const companyFinancials = filterFinancialsByPeriod(companyFinancialsForMonth ?? [], period, fye);

      return getTypedFinancials(companyFinancials, kpiConfigMap);
    }),
  (a, b) => a.companyId === b.companyId && a.date === b.date && a.period === b.period
);

export function filterOutInvalidFinancials(financials: ICompanyFinancialsDataModel[]) {
  return financials.filter((item) => {
    const isInvalid = !isDateString(item.date, RendererType.date);
    if (isInvalid) {
      console.error('invalid financials date', { item });
    }
    return !isInvalid;
  });
}

export function findLatestDateAndPeriod(financials: ICompanyFinancialsDataModel[], fye: number) {
  let period: KpiPeriod | undefined;

  const latestDate = financials.reduce((res: Date | undefined, financial) => {
    if (financial.period && isKpiPeriod(financial.period!) && financial.section === KpiSection.actual) {
      const endOfPeriod = endOfFiscalPeriod(new Date(financial.date), financial.period!, fye - 1);
      if (!isEqualToKpiDate(financial.date, endOfPeriod)) {
        return res;
      }
      const itemDate = new Date(financial.date);
      if (res === undefined) {
        res = itemDate;
        period = financial.period;
      } else {
        const max = getMaxByHierarchy(
          { date: res, period: period! },
          { date: itemDate, period: financial.period! }
        );
        res = max.date;
        period = max.period;
      }
    }
    return res;
  }, undefined);

  return { latestDate, period };
}

/**
 * @param companyFinancials
 * @param period
 * @param fye - [1-12], reflecting company.fye field value range
 * @returns
 */
export function filterFinancialsByPeriod(
  companyFinancials: ICompanyFinancialsDataModel[],
  period: KpiPeriod,
  fye: number
) {
  return companyFinancials.filter((item) => {
    const endOfPeriod = endOfFiscalPeriod(new Date(item.date), period, fye - 1);
    return item.period === period && isEqualToKpiDate(item.date, endOfPeriod);
  });
}

const supportedPeriods = new Set<KpiPeriod>([KpiPeriod.month, KpiPeriod.quarter, KpiPeriod.year]);
export function isKpiPeriod(period?: KpiPeriod | unknown): period is KpiPeriod {
  return Boolean(period) && supportedPeriods.has(period as KpiPeriod);
}

const periodHierarchy = { [KpiPeriod.quarter]: 1, [KpiPeriod.year]: 2, [KpiPeriod.month]: 3 };
interface IDateAndPeriod {
  date: Date;
  period: KpiPeriod;
}
export function getMaxByHierarchy(item1: IDateAndPeriod, item2: IDateAndPeriod): IDateAndPeriod {
  if (!isEqual(item1.date, item2.date)) {
    const latestDate = max([item1.date, item2.date]);
    return isEqual(latestDate, item1.date) ? item1 : item2;
  }
  return periodHierarchy[item1.period as keyof typeof periodHierarchy] <
    periodHierarchy[item2.period as keyof typeof periodHierarchy]
    ? item1
    : item2;
}
